class DirectLiquidSlider {
  constructor() {
    this.currentIndex = 0;
    this.scrollPosition = 0;
    this.mediaData = [];
    
    // タッチ判定用
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    
    // サムネイルスワイプ用
    this.thumbnailTouchStartX = 0;
    this.thumbnailTouchStartY = 0;
    this.thumbnailScrollIndex = 0;
    this.thumbnailStartScrollPosition = 0;
    this.thumbnailIsSwiping = false;
    
    this.init();
  }

  init() {
    try {
      this.extractDataFromGridSections();
      
      if (this.mediaData.length > 0) {
        this.createSliderInterface();
        this.setupEvents();
        this.selectMedia(0);
      } else {
        this.showNotAvailable();
      }
    } catch (error) {
      this.showError('スライダーの初期化に失敗しました');
    }
  }

  getMediaComposition() {
    const imageCount = this.mediaData.filter(item => item.type === 'image').length;
    const videoCount = this.mediaData.filter(item => item.type === 'video').length;
    return `画像: ${imageCount}個, 動画: ${videoCount}個`;
  }

  extractDataFromGridSections() {
    const sections = document.querySelectorAll('.original-img-area');
    
    sections.forEach((section, index) => {
      const img = section.querySelector('.original-img-area img');
      const videoElement = section.querySelector('.original-img-area video');
      const videoSource = section.querySelector('.original-img-area video source')
      const infoArea = section.querySelector('.original-info-area');
      
      if ((img && img.src) || (videoSource && videoSource.src)) {
        const metadata = this.extractInfoFromArea(infoArea);
        
        let mediaData;
        if (videoElement && videoSource && videoSource.src) {
          mediaData = this.createVideoData(videoElement, videoSource, metadata, index);
        } else if (img && img.src) {
          const srcLower = img.src.toLowerCase();
          const isVideoFile = /\.(mp4|webm|mov|avi)(\?|$)/i.test(srcLower);
          
          if (isVideoFile) {
            mediaData = this.createVideoDataFromImg(img, metadata, index);
          } else {
            mediaData = this.createImageData(img, metadata, index);
          }
        }
        
        this.mediaData.push(mediaData);
      }
    });
  }

  createVideoDataFromImg(img, metadata, index) {
    return {
      type: 'video',
      src: img.src,
      poster:'',
      alt: img.alt || `動画${index + 1}`,
      model: metadata.model || '',
      size: metadata.size || '',
      color: metadata.color || ''
    };
  }
  
  createVideoData(videoElement, videoSource, metadata, index) {
    return {
      type: 'video',
      src: videoSource ? videoSource.src : '', 
      poster: videoElement.poster || '', 
      alt: videoElement.title || `動画${index + 1}`,
      model: metadata.model || '',
      size: metadata.size || '',
      color: metadata.color || ''
    };
  }

  createImageData(img, metadata, index) {
    const highResImageUrl = this.generateHighResImageUrl(img.src);
    const srcsetData = this.generateSrcset(img.src);
    
    return {
      type: 'image',
      src: highResImageUrl,
      srcset: srcsetData.srcset,
      sizes: srcsetData.sizes,
      alt: img.alt || `画像${index + 1}`,
      model: metadata.model || '',
      size: metadata.size || '',
      color: metadata.color || ''
    };
  }

  generateHighResImageUrl(originalSrc) {
    const baseUrl = originalSrc.split('?')[0];
    const urlParams = new URLSearchParams(originalSrc.split('?')[1] || '');
    
    urlParams.delete('width');
    urlParams.set('width', '1200');
    
    return `${baseUrl}?${urlParams.toString()}`;
  }

  generateSrcset(originalSrc) {
    const baseUrl = originalSrc.split('?')[0];
    const urlParams = new URLSearchParams(originalSrc.split('?')[1] || '');
    
    urlParams.delete('width');
    
    const baseSrc = `${baseUrl}?${urlParams.toString()}`;
    
    const widths = [400, 600, 800, 1000, 1200, 1400, 1600];
    const srcsetArray = widths.map(width => `${baseSrc}&width=${width} ${width}w`);
    
    const srcset = srcsetArray.join(', ');
    const sizes = '(min-width: 1300px) 660px, (min-width: 990px) calc(55.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw / 1 - 4rem)';
    
    return { srcset, sizes };
  }

  generateThumbnailUrl(originalSrc) {
    if (this.isVideoFile(originalSrc)) {
      return this.getDefaultVideoThumbnail();
    }
    
    if (!originalSrc.includes('?')) {
      return originalSrc;
    }
    
    const baseUrl = originalSrc.split('?')[0];
    const urlParams = new URLSearchParams(originalSrc.split('?')[1] || '');
    
    urlParams.set('width', '200');
    
    return `${baseUrl}?${urlParams.toString()}`;
  }

  isVideoFile(src) {
    return /\.(mp4|webm|mov|avi|m4v)(\?|$)/i.test(src);
  }

  getDefaultVideoThumbnail() {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHBvbHlnb24gcG9pbnRzPSIxMDAsNzUgNzAsOTAgNzAsNjAiIGZpbGw9IiNmZmYiLz48L3N2Zz4=';
  }

  generateVideoPosterUrl(videoSrc) {
    if (!videoSrc) return '';
    
    const baseUrl = videoSrc.split('?')[0];
    const extension = baseUrl.split('.').pop();
    
    if (['mp4', 'webm', 'mov'].includes(extension.toLowerCase())) {
      const posterUrl = baseUrl.replace(`.${extension}`, '_poster.jpg');
      return this.generateThumbnailUrl(posterUrl);
    }
    
    return videoSrc;
  }
  
  extractInfoFromArea(infoArea) {
    const metadata = { model: '', size: '', color: '' };
    
    if (!infoArea) return metadata;
    
    const paragraphs = infoArea.querySelectorAll('p');
    paragraphs.forEach(p => {
      const text = p.textContent.trim();
      
      if (text.includes('モデル:')) {
        metadata.model = text.replace('モデル:', '').trim();
      } else if (text.includes('サイズ:')) {
        metadata.size = text.replace('サイズ:', '').trim();
      } else if (text.includes('カラー:')) {
        metadata.color = text.replace('カラー:', '').trim();
      }
    });
    
    return metadata;
  }

  createSliderInterface() {
    const container = document.getElementById('sliderContainer');
    
    if (!container) {
      const mediaWrapper = document.querySelector('.product__media-wrapper');
      if (mediaWrapper) {
        const fallbackContainer = document.createElement('div');
        fallbackContainer.className = 'slider-container';
        fallbackContainer.id = 'sliderContainer';
        mediaWrapper.appendChild(fallbackContainer);
        this.createSliderContent(fallbackContainer);
      } else {
        return;
      }
    } else {
      this.createSliderContent(container);
    }
  }

  createSliderContent(container) {
    container.innerHTML = `
      <section class="slider-main-section">
        <div class="slider-main-image" id="sliderMainImage">
          <img id="mainImage" src="" alt="メイン画像" style="display: none;" />
          <video id="mainVideo" controls muted style="display: none;">
            お使いのブラウザは動画をサポートしていません。
          </video>
          <div class="slider-nav-hint left">←</div>
          <div class="slider-nav-hint right">→</div>
        </div>
        <div class="slider-main-info" id="sliderMainInfo">
          <!-- 動的に生成 -->
        </div>
      </section>
      
      <div class="slider-thumbnail-container">
        <button class="slider-nav-btn" id="nextBtn">←</button>
        <div class="slider-thumbnail-viewport">
          <div class="slider-thumbnail-wrapper" id="thumbnailWrapper">
            <!-- 動的に生成 -->
          </div>
        </div>
        <button class="slider-nav-btn" id="prevBtn">→</button>
      </div>
    `;
  
    this.generateThumbnails();
  }

  generateThumbnails() {
    const wrapper = document.getElementById('thumbnailWrapper');
    
    this.mediaData.forEach((data, index) => {
      const thumbnail = document.createElement('div');
      thumbnail.className = `slider-thumbnail ${index === 0 ? 'active' : ''}`;
      thumbnail.setAttribute('data-index', index);
      
      const infoItems = [data.model, data.size, data.color].filter(item => item.trim());
      
      
      const thumbnailSrc = this.generateThumbnailSource(data);
  
      thumbnail.innerHTML = `
        <div class="slider-thumbnail-image">
          <img src="${thumbnailSrc}" alt="${data.alt}" />
          ${data.type === 'video' ? '<div class="video-indicator">▶</div>' : ''}
        </div>
      `;
      
      thumbnail.addEventListener('click', () => this.selectMedia(index));
      wrapper.appendChild(thumbnail);
    });
  }

  generateThumbnailSource(data) {
    try {
      if (data.type === 'video') {
        if (data.poster && data.poster.trim()) {
          return this.generateThumbnailUrl(data.poster);
        } else {
          return this.getDefaultVideoThumbnail();
        }
      } else {
        return this.generateThumbnailUrl(data.src);
      }
    } catch (error) {
      return this.getDefaultVideoThumbnail();
    }
  }

  isVideoControl(element) {
    return element.tagName === 'VIDEO' || 
           element.closest('video') || 
           element.classList.contains('video-controls') ||
           element.tagName === 'SOURCE';
  }

  showNotAvailable() {
    const container = document.getElementById('sliderContainer');
    if (container) {
      container.innerHTML = `
        <div class="slider-not-available">
          <p>この商品にはモデル着用画像がありません</p>
          <small>画像または動画が見つかりませんでした</small>
        </div>
      `;
    }
  }

  setupEvents() {
    this.mainImageElement = document.getElementById('sliderMainImage');
    this.mainImage = document.getElementById('mainImage');
    this.mainVideo = document.getElementById('mainVideo');
    this.mainInfo = document.getElementById('sliderMainInfo');
    this.thumbnailWrapper = document.getElementById('thumbnailWrapper');
    this.thumbnailViewport = document.querySelector('.slider-thumbnail-viewport');
    this.prevBtn = document.getElementById('prevBtn');
    this.nextBtn = document.getElementById('nextBtn');

    this.setupMainImageEvents();
    this.setupThumbnailSwipeEvents();
    
    this.prevBtn.addEventListener('click', () => this.scrollThumbnails('left'));
    this.nextBtn.addEventListener('click', () => this.scrollThumbnails('right'));
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.navigate('prev');
      if (e.key === 'ArrowRight') this.navigate('next');
    });
    
    window.addEventListener('resize', () => this.updateLayout());
  }

  setupThumbnailSwipeEvents() {
    if (!this.thumbnailViewport) return;
    
    const isMobile = () => window.innerWidth <= 768;
    
    this.thumbnailViewport.addEventListener('touchstart', (e) => {
      if (!isMobile()) return;
      
      const touch = e.touches[0];
      this.thumbnailTouchStartX = touch.clientX;
      this.thumbnailTouchStartY = touch.clientY;
      this.thumbnailStartScrollPosition = this.scrollPosition;
      this.thumbnailIsSwiping = false;
      
      this.thumbnailWrapper.style.transition = 'none';
    }, { passive: true });
    
    this.thumbnailViewport.addEventListener('touchmove', (e) => {
      if (!isMobile()) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - this.thumbnailTouchStartX;
      const deltaY = Math.abs(touch.clientY - this.thumbnailTouchStartY);
      
      if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
        e.preventDefault();
        this.thumbnailIsSwiping = true;
        
        let newPosition = this.thumbnailStartScrollPosition - deltaX;
        
        const containerWidth = this.thumbnailViewport.offsetWidth;
        const maxScroll = this.thumbnailWrapper.scrollWidth - containerWidth;
        
        if (newPosition < 0) {
          newPosition = newPosition * 0.3;
        } else if (newPosition > maxScroll) {
          newPosition = maxScroll + (newPosition - maxScroll) * 0.3;
        }
        
        this.scrollPosition = newPosition;
        this.thumbnailWrapper.style.transform = `translateX(-${this.scrollPosition}px)`;
      }
    }, { passive: false });
    
    this.thumbnailViewport.addEventListener('touchend', (e) => {
      if (!isMobile()) return;
      
      this.thumbnailWrapper.style.transition = 'transform 0.3s ease';
      
      if (!this.thumbnailIsSwiping) return;
      
      const touch = e.changedTouches[0];
      const deltaX = this.thumbnailTouchStartX - touch.clientX;
      const swipeDistance = Math.abs(deltaX);
      
      if (swipeDistance > 30) {
        if (deltaX > 0) {
          this.smoothScrollThumbnails('next');
        } else {
          this.smoothScrollThumbnails('prev');
        }
      } else {
        this.snapToNearestThumbnail();
      }
      
      this.thumbnailIsSwiping = false;
    }, { passive: true });
  }

  smoothScrollThumbnails(direction) {
    const thumbnails = this.thumbnailWrapper.querySelectorAll('.slider-thumbnail');
    if (thumbnails.length === 0) return;

    const containerWidth = this.thumbnailViewport.offsetWidth;
    const containerCenter = containerWidth / 2;
    let currentCenterIndex = 0;
    let minDistance = Infinity;

    thumbnails.forEach((thumbnail, index) => {
      const thumbnailLeft = thumbnail.offsetLeft;
      const thumbnailCenter = thumbnailLeft + thumbnail.offsetWidth / 2 - this.scrollPosition;
      const distance = Math.abs(thumbnailCenter - containerCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        currentCenterIndex = index;
      }
    });

    let targetIndex;
    if (direction === 'next') {
      targetIndex = currentCenterIndex >= thumbnails.length - 1 ? 0 : currentCenterIndex + 1;
    } else {
      targetIndex = currentCenterIndex <= 0 ? thumbnails.length - 1 : currentCenterIndex - 1;
    }

    this.scrollToThumbnailIndex(targetIndex);
  }

  scrollToThumbnailIndex(index) {
    const thumbnails = this.thumbnailWrapper.querySelectorAll('.slider-thumbnail');
    if (!thumbnails[index]) return;

    const containerWidth = this.thumbnailViewport.offsetWidth;
    const thumbnail = thumbnails[index];
    const thumbnailLeft = thumbnail.offsetLeft;
    const thumbnailWidth = thumbnail.offsetWidth;
    
    let targetPosition = thumbnailLeft - (containerWidth - thumbnailWidth) / 2;
    
    const maxScroll = this.thumbnailWrapper.scrollWidth - containerWidth;
    targetPosition = Math.max(0, Math.min(maxScroll, targetPosition));
    
    this.scrollPosition = targetPosition;
    this.thumbnailScrollIndex = index;
    this.thumbnailWrapper.style.transform = `translateX(-${this.scrollPosition}px)`;
  }

  scrollThumbnails(direction) {
    const thumbnails = this.thumbnailWrapper.querySelectorAll('.slider-thumbnail');
    const containerWidth = this.thumbnailViewport.offsetWidth;
    
    const scrollAmount = containerWidth * 0.7;
    
    if (direction === 'left') {
      const maxScroll = this.thumbnailWrapper.scrollWidth - containerWidth;
      if (this.scrollPosition >= maxScroll) {
        this.scrollPosition = 0;
      } else {
        this.scrollPosition = Math.min(maxScroll, this.scrollPosition + scrollAmount);
      }
    } else {
      if (this.scrollPosition <= 0) {
        const maxScroll = this.thumbnailWrapper.scrollWidth - containerWidth;
        this.scrollPosition = maxScroll;
      } else {
        this.scrollPosition = Math.max(0, this.scrollPosition - scrollAmount);
      }
    }
    
    this.thumbnailWrapper.style.transform = `translateX(-${this.scrollPosition}px)`;
  }

  setupMainImageEvents() {
    let touchHandled = false;

    this.mainImageElement.addEventListener('click', (e) => {
      if (touchHandled) {
        touchHandled = false;
        return;
      }
      this.handleClick(e);
    });

    this.mainImageElement.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartTime = Date.now();
    });

    this.mainImageElement.addEventListener('touchend', (e) => {
      const touch = e.changedTouches[0];
      const endX = touch.clientX;
      const endY = touch.clientY;
      const endTime = Date.now();

      const deltaX = Math.abs(endX - this.touchStartX);
      const deltaY = Math.abs(endY - this.touchStartY);
      const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const touchDuration = endTime - this.touchStartTime;

      const isIntentionalTap = totalDistance < 20 && touchDuration < 500;

      if (isIntentionalTap) {
        touchHandled = true;
        e.preventDefault();
        this.handleClick({
          clientX: this.touchStartX,
          clientY: this.touchStartY
        });
      }
    });
  }

  handleClick(e) {
    const rect = this.mainImageElement.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const centerX = rect.width / 2;
    
    if (clickX < centerX) {
      this.navigate('prev');
    } else {
      this.navigate('next');
    }
  }

  navigate(direction) {
    if (this.mediaData.length <= 1) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = this.currentIndex === 0 ? this.mediaData.length - 1 : this.currentIndex - 1;
    } else {
      newIndex = this.currentIndex === this.mediaData.length - 1 ? 0 : this.currentIndex + 1;
    }
    
    this.selectMedia(newIndex);
  }

  selectMedia(index) {
    try {
      if (index < 0 || index >= this.mediaData.length) return;
      
      const data = this.mediaData[index];
      
      this.updateMainMedia(data);
      this.updateMainInfo(data);
      this.updateThumbnails(index);
      
      this.currentIndex = index;
      this.ensureThumbnailVisible(index);
    } catch (error) {
      this.selectMedia(0);
    }
  }

  updateMainMedia(data) {
    if (data.type === 'video') {
      this.showVideo(data);
    } else {
      this.showImage(data);
    }
  }
  
  showImage(data) {
    try {
      this.mainVideo.style.display = 'none';
      if (!this.mainVideo.paused) {
        this.mainVideo.pause();
      }
      
      this.mainImage.src = data.src;
      this.mainImage.srcset = data.srcset || '';
      this.mainImage.sizes = data.sizes || '';
      this.mainImage.alt = data.alt;
      this.mainImage.style.display = 'block';
      
      this.mainImage.onerror = () => {
        this.showImageError();
      };
    } catch (error) {
      this.showImageError();
    }
  }

  showVideo(data) {
    try {
      this.mainImage.style.display = 'none';

      this.mainVideo.src = data.src;
      this.mainVideo.poster = data.poster || '';
      this.mainVideo.title = data.alt;
      this.mainVideo.style.display = 'block';

      this.mainVideo.muted = true;
      this.mainVideo.autoplay = true;
      this.mainVideo.loop = true;
      this.mainVideo.controls = false;
      this.mainVideo.preload = 'metadata';

      this.mainVideo.setAttribute('playsinline', '');
      this.mainVideo.playsInline = true;
      this.mainVideo.setAttribute('autoplay', '');
      this.mainVideo.setAttribute('loop', '');
      this.mainVideo.setAttribute('muted', '');
      this.mainVideo.removeAttribute('controls');

      this.mainVideo.style.width = '100%';
      this.mainVideo.style.maxWidth = '100%';
      this.mainVideo.style.objectFit = 'contain';
      
      this.mainVideo.onerror = () => {
        this.showVideoError();
      };
    } catch (error) {
      this.showVideoError();
    }
  }

  showVideoError() {
    this.mainVideo.style.display = 'none';
    this.mainImage.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWLleeUu+OCkuiql+OBv+i+vOOCgeOBvuOBm+OCk+OBp+OBl+OBnzwvdGV4dD48L3N2Zz4=';
    this.mainImage.alt = '動画を読み込めませんでした';
    this.mainImage.style.display = 'block';
  }

  showImageError() {
    this.mainImage.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPueUu+WDj+OCkuiql+OBv+i+vOOCgeOBvuOBm+OCk+OBp+OBl+OBnzwvdGV4dD48L3N2Zz4=';
    this.mainImage.alt = '画像を読み込めませんでした';
  }

  showError(message) {
    const container = document.getElementById('sliderContainer');
    if (container) {
      container.innerHTML = `
        <div class="slider-error">
          <p>⚠️ ${message}</p>
          <button onclick="location.reload()">ページを再読み込み</button>
        </div>
      `;
    }
  }

  updateMainInfo(data) {
    this.mainInfo.innerHTML = '';
    
    if (data.model.trim()) {
      const div = document.createElement('div');
      div.innerHTML = `<span>モデル: ${data.model}</span>`;
      this.mainInfo.appendChild(div);
    }
    
    if (data.size.trim()) {
      const div = document.createElement('div');
      div.innerHTML = `<span>サイズ: ${data.size}</span>`;
      this.mainInfo.appendChild(div);
    }
    
    if (data.color.trim()) {
      const div = document.createElement('div');
      div.innerHTML = `<span>カラー: ${data.color}</span>`;
      this.mainInfo.appendChild(div);
    }
  }

  updateThumbnails(activeIndex) {
    const thumbnails = this.thumbnailWrapper.querySelectorAll('.slider-thumbnail');
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle('active', index === activeIndex);
    });
  }

  ensureThumbnailVisible(index) {
    this.scrollToThumbnailIndex(index);
  }

  updateLayout() {
    this.scrollPosition = 0;
    this.thumbnailScrollIndex = 0;
    this.thumbnailWrapper.style.transform = 'translateX(0)';
  }
}

// 初期化
document.addEventListener('DOMContentLoaded', () => {
  new DirectLiquidSlider();
});